import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// VCC Logo as base64 (will be loaded dynamically)
let vccLogoBase64 = null;
let vccWatermarkBase64 = null;
let logosLoaded = false;

// Load logo images
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        console.warn('Canvas error:', e);
        resolve(null);
      }
    };
    img.onerror = (e) => {
      console.warn('Image load error:', e);
      resolve(null); // Resolve with null instead of rejecting
    };
    img.src = src;
  });
};

// Initialize logos - try to load but don't fail if they don't load
const initLogos = async () => {
  if (logosLoaded) return;
  
  try {
    // Use absolute URL to ensure proper loading
    const baseUrl = window.location.origin;
    // Use the horizontal logo with "V Cosmetic Crafters" text
    vccLogoBase64 = await loadImage(`${baseUrl}/vcc-logo-horizontal.png`);
    vccWatermarkBase64 = await loadImage(`${baseUrl}/vcc-watermark.png`);
    logosLoaded = true;
  } catch (error) {
    console.warn('Error loading logos:', error);
    logosLoaded = true; // Mark as loaded to prevent retry
  }
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Generate PDF for a specific iteration
export const generateIterationPDF = async (product, iteration) => {
  await initLogos();
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Helper function to add new page (no watermark)
  const addNewPage = () => {
    doc.addPage();
    yPosition = margin;
  };

  // Helper function to check page break
  const checkPageBreak = (height) => {
    if (yPosition + height > pageHeight - margin) {
      addNewPage();
      return true;
    }
    return false;
  };

  // ========== PROFESSIONAL HEADER ==========
  
  // Draw header background - light gray strip
  doc.setFillColor(248, 249, 250);
  doc.rect(0, 0, pageWidth, 52, 'F');
  
  // Draw bottom border line
  doc.setDrawColor(38, 71, 124);
  doc.setLineWidth(0.5);
  doc.line(margin, 52, pageWidth - margin, 52);

  // Add VCC Logo - centered in header
  if (vccLogoBase64) {
    try {
      // Center the logo horizontally
      const logoWidth = 65;
      const logoHeight = 16;
      const logoX = (pageWidth - logoWidth) / 2;
      doc.addImage(vccLogoBase64, 'PNG', logoX, 6, logoWidth, logoHeight);
    } catch (e) {
      console.warn('Logo error:', e);
    }
  }
  
  // Product Name - centered below logo
  doc.setFontSize(14);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'bold');
  const productNameText = product.productName || 'Product Development';
  const productNameWidth = doc.getTextWidth(productNameText);
  doc.text(productNameText, (pageWidth - productNameWidth) / 2, 30);
  
  // "Product Development Report" subtitle - centered below product name
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  const subtitleText = 'Product Development Report';
  const subtitleWidth = doc.getTextWidth(subtitleText);
  doc.text(subtitleText, (pageWidth - subtitleWidth) / 2, 38);
  
  // Sample No - centered below subtitle
  doc.setFontSize(9);
  doc.setTextColor(38, 71, 124);
  doc.setFont('helvetica', 'bold');
  const sampleText = `Sample No: ${product.sampleNo || '-'}`;
  const sampleWidth = doc.getTextWidth(sampleText);
  doc.text(sampleText, (pageWidth - sampleWidth) / 2, 46);
  
  yPosition = 62;

  // ========== ITERATION HEADER BANNER ==========
  doc.setFillColor(38, 71, 124);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 14, 2, 2, 'F');
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  const iterationText = `Iteration ${iteration.iterationNo} ${iteration.formulaVersion ? '- ' + iteration.formulaVersion : ''}`;
  doc.text(iterationText, margin + 5, yPosition + 9);
  
  // Add date on the right side of banner
  const dateText = formatDate(iteration.date);
  const dateWidth = doc.getTextWidth(dateText);
  doc.setFontSize(10);
  doc.text(dateText, pageWidth - margin - dateWidth - 5, yPosition + 9);
  
  yPosition += 22;

  // Product Information Section
  doc.setFontSize(12);
  doc.setTextColor(38, 71, 124);
  doc.setFont('helvetica', 'bold');
  doc.text('Product Information', margin, yPosition);
  yPosition += 8;

  const productInfo = [
    ['Product Name', product.productName || '-'],
    ['Sample No', product.sampleNo || '-'],
    ['Client Name', product.clientName || '-'],
    ['SKU', iteration.sku || product.sku || '-'],
    ['Date', formatDate(iteration.date)],
    ['Created By', iteration.createdBy || '-'],
    ['Test Status', iteration.testStatus || '-'],
    ['Result', iteration.result || 'Pending']
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: productInfo,
    theme: 'plain',
    styles: {
      fontSize: 10,
      cellPadding: 3
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40, textColor: [100, 100, 100] },
      1: { cellWidth: 'auto', textColor: [50, 50, 50] }
    },
    margin: { left: margin, right: margin }
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Formula Details Section
  if (iteration.productDetails && iteration.productDetails.length > 0) {
    checkPageBreak(20);
    
    doc.setFontSize(12);
    doc.setTextColor(38, 71, 124);
    doc.setFont('helvetica', 'bold');
    doc.text(`Formula Details (${iteration.productDetails.length} ingredients)`, margin, yPosition);
    yPosition += 6;

    const formulaData = iteration.productDetails.map((detail, idx) => [
      idx + 1,
      detail.items || detail.ingredient || '-',
      detail.inci || detail.tradeName || '-',
      detail.composition || detail.quantity || '-',
      detail.function || '-',
      detail.vendorDetails || detail.supplier || '-'
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['#', 'Ingredient', 'INCI Name', 'Comp %', 'Function', 'Supplier']],
      body: formulaData,
      theme: 'striped',
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [38, 71, 124],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 40 },
        3: { cellWidth: 15, halign: 'right' },
        4: { cellWidth: 30 },
        5: { cellWidth: 35 }
      },
      margin: { left: margin, right: margin }
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Testing Details Section
  if (iteration.testingDetails && Object.keys(iteration.testingDetails).length > 0) {
    checkPageBreak(30);
    
    doc.setFontSize(12);
    doc.setTextColor(38, 71, 124);
    doc.setFont('helvetica', 'bold');
    doc.text('Testing Details', margin, yPosition);
    yPosition += 8;

    // Quick stats
    const quickStats = [];
    if (iteration.testingDetails.phInitial) quickStats.push(['pH Initial', iteration.testingDetails.phInitial]);
    if (iteration.testingDetails.phFinal) quickStats.push(['pH Final', iteration.testingDetails.phFinal]);
    if (iteration.testingDetails.phRange) quickStats.push(['pH Range', iteration.testingDetails.phRange]);
    if (iteration.testingDetails.viscosity) quickStats.push(['Viscosity', iteration.testingDetails.viscosity]);
    if (iteration.testingDetails.color) quickStats.push(['Color', iteration.testingDetails.color]);
    if (iteration.testingDetails.texture) quickStats.push(['Texture', iteration.testingDetails.texture]);
    if (iteration.testingDetails.odour) quickStats.push(['Odour', iteration.testingDetails.odour]);
    if (iteration.testingDetails.testResult) quickStats.push(['Test Result', iteration.testingDetails.testResult]);

    if (quickStats.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [],
        body: quickStats,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 30, fillColor: [240, 240, 240] },
          1: { cellWidth: 40 }
        },
        margin: { left: margin, right: margin }
      });
      yPosition = doc.lastAutoTable.finalY + 8;
    }

    // Description
    if (iteration.testingDetails.description) {
      checkPageBreak(25);
      doc.setFontSize(10);
      doc.setTextColor(38, 71, 124);
      doc.setFont('helvetica', 'bold');
      doc.text('Description:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      const descLines = doc.splitTextToSize(iteration.testingDetails.description, pageWidth - 2 * margin);
      doc.text(descLines, margin, yPosition);
      yPosition += descLines.length * 5 + 5;
    }

    // Direction of Use
    if (iteration.testingDetails.directionOfUse) {
      checkPageBreak(25);
      doc.setFontSize(10);
      doc.setTextColor(38, 71, 124);
      doc.setFont('helvetica', 'bold');
      doc.text('Direction of Use:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      const dirLines = doc.splitTextToSize(iteration.testingDetails.directionOfUse, pageWidth - 2 * margin);
      doc.text(dirLines, margin, yPosition);
      yPosition += dirLines.length * 5 + 5;
    }

    // Method of Preparation
    if (iteration.testingDetails.methodOfPreparation) {
      checkPageBreak(30);
      doc.setFontSize(10);
      doc.setTextColor(38, 71, 124);
      doc.setFont('helvetica', 'bold');
      doc.text('Method of Preparation:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(8);
      const methodLines = doc.splitTextToSize(iteration.testingDetails.methodOfPreparation, pageWidth - 2 * margin);
      
      // Handle multiple pages for long method
      for (let i = 0; i < methodLines.length; i++) {
        if (yPosition > pageHeight - margin - 5) {
          addNewPage();
        }
        doc.text(methodLines[i], margin, yPosition);
        yPosition += 4;
      }
      yPosition += 5;
    }

    // Observation
    if (iteration.testingDetails.observation) {
      checkPageBreak(25);
      doc.setFontSize(10);
      doc.setTextColor(38, 71, 124);
      doc.setFont('helvetica', 'bold');
      doc.text('Observation:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      const obsLines = doc.splitTextToSize(iteration.testingDetails.observation, pageWidth - 2 * margin);
      doc.text(obsLines, margin, yPosition);
      yPosition += obsLines.length * 5 + 5;
    }

    // Testing Remarks
    if (iteration.testingDetails.remarks) {
      checkPageBreak(30);
      doc.setFontSize(10);
      doc.setTextColor(180, 100, 0); // Amber color
      doc.setFont('helvetica', 'bold');
      doc.text('Testing Remarks:', margin, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(8);
      const remLines = doc.splitTextToSize(iteration.testingDetails.remarks, pageWidth - 2 * margin);
      
      for (let i = 0; i < remLines.length; i++) {
        if (yPosition > pageHeight - margin - 5) {
          addNewPage();
        }
        doc.text(remLines[i], margin, yPosition);
        yPosition += 4;
      }
      yPosition += 5;
    }
  }

  // Iteration Remarks
  if (iteration.remarks) {
    checkPageBreak(20);
    doc.setFontSize(10);
    doc.setTextColor(180, 100, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Iteration Remarks:', margin, yPosition);
    yPosition += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    const itRemLines = doc.splitTextToSize(iteration.remarks, pageWidth - 2 * margin);
    doc.text(itRemLines, margin, yPosition);
    yPosition += itRemLines.length * 5 + 5;
  }

  // ========== PROFESSIONAL FOOTER ==========
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    
    // Company name on left
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('V Cosmetic Crafters Private Limited', margin, pageHeight - 8);
    
    // Page number in center
    doc.setFont('helvetica', 'bold');
    const pageText = `Page ${i} of ${totalPages}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, (pageWidth - pageTextWidth) / 2, pageHeight - 8);
    
    // Generated date on right
    doc.setFont('helvetica', 'normal');
    const dateStr = new Date().toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const dateWidth = doc.getTextWidth(dateStr);
    doc.text(dateStr, pageWidth - margin - dateWidth, pageHeight - 8);
  }

  // Generate filename
  const filename = `${product.sampleNo}_Iteration_${iteration.iterationNo}_${iteration.formulaVersion || 'V1'}.pdf`;
  
  // Save PDF
  doc.save(filename);
  
  return filename;
};

export default generateIterationPDF;
